// Register definitions for the HR & People sub-modules that are lists over one record type
// (the Stephanet "entity" views). Each resource says how to load, create, edit, move and act on its rows.
//
// field: { name, label, type, list, pill, get (display value), render, form: false|'create'|'edit', required, options, ref, ... }

import * as hr from './api';
import { clearLookup } from './HRContext';
import { daysUntil, fmtDate } from './format';
import { hrSubUrl } from './catalog';
import { documentRule, LEAVE_ATTACHMENT_RULE } from './fileRules';
import { storedFileCell } from './components/Files';

const who = (label = 'Employee', name = 'employee_id') => ({
  name, label, type: 'ref', ref: 'employees', required: true, list: true,
  get: r => (r.employee_name ? `${r.employee_name}${r.employee_no ? ` (${r.employee_no})` : ''}` : r.employee_no || null),
});
const ref = (name, label, lookup, display, extra = {}) => ({ name, label, type: 'ref', ref: lookup, get: r => r[display] || null, ...extra });
const status = (options, extra = {}) => ({ name: 'status', label: 'Status', type: 'select', options, pill: true, list: true, ...extra });
const readonly = f => ({ ...f, form: false });
// an uploaded file on a record: picked in the form, uploaded through its own route once the record is saved
const fileField = (name, label, extra) => ({ name, label, type: 'file', list: true, ...extra,
  upload: (r, file) => (file instanceof File ? extra.upload(r, file) : Promise.reject(new Error('Choose a file first'))),
  get: r => extra.stored(r)?.name || null, render: r => storedFileCell(extra.stored(r), () => extra.open(r)) });

const APPROVALS = ['pending', 'pre_approved', 'approved', 'rejected', 'cancelled'];
// two-step sign-off used by HR cases, overtime and leave: supervisor (pending) then HR (pre-approved)
const TWO_STEP = { pending: ['pre_approved', 'rejected'], pre_approved: ['approved', 'rejected'] };

const expiryText = d => {
  const left = daysUntil(d);
  if (left === null) return 'No Expiry';
  if (left < 0) return 'Expired';
  if (left <= 30) return 'Expiring in 30 days';
  if (left <= 90) return 'Expiring in 90 days';
  return 'Valid';
};

export const DOC_TYPES = ['passport', 'visa', 'work_permit', 'residence_permit', 'cerpac', 'drivers_license', 'id_card', 'cv', 'certificate', 'offer_letter',
  'appointment_letter', 'contract', 'guarantor_form', 'reference_letter', 'bank_proof', 'pension_form', 'hmo_form', 'medical', 'other'];

// the scan of an employee document: the file types allowed follow its document type
const SCAN = fileField('scan', 'Scan', {
  rule: d => documentRule(d.document_type), stored: r => r.file,
  open: r => hr.documentFileBlob(r.id), upload: (r, file) => hr.uploadDocumentFile(r.id, file),
});

const LEAVE_ATTACHMENT = fileField('attachment', 'Supporting Document', {
  list: false, rule: () => LEAVE_ATTACHMENT_RULE, stored: r => r.attachment,
  open: r => hr.leaveAttachmentBlob(r.id), upload: (r, file) => hr.uploadLeaveAttachment(r.id, file),
});

export const RESOURCES = {
  // ── Employee Management ──────────────────────────────────────────────────
  employee_documents: {
    key: 'employee_documents', label: 'Employee Document', title: r => r.title,
    load: () => hr.listDocuments(),
    create: hr.createDocument,
    remove: r => hr.deleteDocument(r.id),
    search: ['employee_name', 'employee_no', 'title', 'document_type', 'document_number'],
    actions: (r, { canManage }) => [
      canManage && { label: r.file ? 'Replace Scan' : 'Attach Scan', fields: [SCAN], initial: x => x, run: (x, _, data) => SCAN.upload(x, data.scan), done: 'Scan uploaded' },
      canManage && r.file && { label: 'Remove Scan', tone: 'danger', confirm: 'Remove the uploaded scan?', run: x => hr.deleteDocumentFile(x.id), done: 'Scan removed' },
      canManage && !r.is_verified && { label: 'Mark Verified', tone: 'primary', run: x => hr.verifyDocument(x.id), done: 'Document verified' },
    ],
    fields: [
      who(),
      { name: 'document_type', label: 'Document Type', type: 'select', options: DOC_TYPES, required: true, list: true },
      { name: 'title', label: 'Title', required: true, list: true },
      { name: 'document_number', label: 'Document Number', list: true },
      { name: 'issuing_authority', label: 'Issuing Authority' },
      { name: 'issue_date', label: 'Issue Date', type: 'date' },
      { name: 'expiry_date', label: 'Expiry Date', type: 'date', list: true },
      { name: 'requires_renewal', label: 'Requires Renewal', type: 'boolean' },
      SCAN,
      // older records may carry a link instead of an upload
      readonly({ name: 'file_url', label: 'File Link', detail: false }),
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
      readonly({ name: 'expiry_state', label: 'Expiry', pill: true, list: true, get: r => expiryText(r.expiry_date) }),
      readonly({ name: 'is_verified', label: 'Verified', type: 'boolean', list: true }),
    ],
  },

  site_accesses: {
    key: 'site_accesses', label: 'Site Access', plural: 'Site access permits', title: r => `${r.employee_name || ''} · ${r.site_name || ''}`,
    load: () => hr.listSitePermits(),
    create: hr.issueSitePermit,
    statusField: 'status',
    search: ['employee_name', 'employee_no', 'site_name', 'access_type'],
    fields: [
      who(),
      ref('site_id', 'Project Site', 'sites', 'site_name', { required: true, list: true }),
      { name: 'access_type', label: 'Access Type', type: 'select', options: ['full_time', 'rotational', 'visitor', 'contractor', 'hazardous_zone'], default: 'full_time', list: true },
      { name: 'start_date', label: 'Start Date', type: 'date', list: true },
      { name: 'end_date', label: 'End Date', type: 'date', list: true },
      { name: 'hse_induction_passed', label: 'HSE Induction Passed', type: 'boolean', list: true },
      { name: 'medical_fitness_passed', label: 'Medical Fitness Passed', type: 'boolean' },
      { name: 'ppe_issued', label: 'PPE Issued', type: 'boolean' },
      { name: 'hazard_allowance_eligible', label: 'Hazard Allowance Eligible', type: 'boolean' },
      status(['active', 'expired', 'revoked'], { default: 'active' }),
    ],
  },

  employee_dependents: {
    key: 'employee_dependents', label: 'Dependant', title: r => r.full_name,
    load: () => hr.listDependents(),
    create: ({ employee_id, ...body }) => hr.addDependent(employee_id, body),
    update: (r, { employee_id, ...body }) => hr.updateDependent(r.id, body),
    remove: r => hr.deleteDependent(r.id),
    search: ['full_name', 'employee_name', 'employee_no', 'relationship_type'],
    detailLink: r => ({ to: hrSubUrl('employee-360', { id: r.employee_id }), label: 'Open Employee 360' }),
    fields: [
      { ...who(), form: 'create' },
      { name: 'full_name', label: 'Full Name', required: true, list: true },
      { name: 'relationship_type', label: 'Relationship', required: true, list: true, suggestions: ['Spouse', 'Son', 'Daughter', 'Ward', 'Parent', 'Sibling'] },
      { name: 'date_of_birth', label: 'Date of Birth', type: 'date', list: true },
      { name: 'gender', label: 'Gender', type: 'select', options: ['male', 'female', 'other'], list: true },
      { name: 'hmo_covered', label: 'HMO Covered', type: 'boolean', list: true },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },

  // ── Talent & Movement ────────────────────────────────────────────────────
  applicants: {
    key: 'applicants', label: 'Applicant', title: r => r.full_name,
    load: () => hr.listApplicants(),
    create: hr.createApplicant,
    update: (r, body) => hr.updateApplicant(r.id, body),
    remove: r => hr.deleteApplicant(r.id),
    statusField: 'stage',
    search: ['full_name', 'email', 'position_applied', 'department_name', 'applicant_no'],
    detailLink: r => (r.hired_employee_id ? { to: hrSubUrl('employee-360', { id: r.hired_employee_id }), label: 'Open hired employee' } : null),
    actions: (r, { canManage }) => [canManage && r.stage !== 'hired' && r.stage !== 'rejected' && {
      label: 'Hire', tone: 'primary', done: `${r.full_name} hired — employee record created on probation`,
      fields: [
        { name: 'hire_date', label: 'Hire Date', type: 'date', required: true, default: () => new Date().toISOString().slice(0, 10) },
        { name: 'basic_salary', label: 'Basic Salary (monthly)', type: 'money' },
        { name: 'primary_site_id', label: 'Primary Project Site', type: 'ref', ref: 'sites' },
      ],
      initial: x => ({ basic_salary: x.expected_salary }),
      run: async (x, body) => { await hr.hireApplicant(x.id, body); clearLookup('employees'); },
    }],
    fields: [
      readonly({ name: 'applicant_no', label: 'Applicant No' }),
      { name: 'full_name', label: 'Full Name', required: true, list: true },
      { name: 'position_applied', label: 'Position Applied', required: true, list: true },
      ref('department_id', 'Department', 'departments', 'department_name', { list: true }),
      ref('designation_id', 'Designation', 'designations', 'designation_title'),
      { name: 'email', label: 'Email', type: 'email', required: true, list: true },
      { name: 'phone', label: 'Phone' },
      { name: 'expected_salary', label: 'Expected Salary', type: 'money', list: true },
      { name: 'rating', label: 'Rating (1–5)', type: 'integer', min: 1, max: 5 },
      { name: 'resume_url', label: 'Résumé Link', placeholder: 'https://…' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
      { name: 'stage', label: 'Stage', type: 'select', options: ['applied', 'screening', 'interview', 'offered', 'hired', 'rejected'], default: 'applied', pill: true, list: true, noBlank: true },
    ],
  },

  hr_cases: {
    key: 'hr_cases', label: 'HR Case', title: r => `${r.case_number} · ${r.title}`,
    load: f => hr.listCases(f),
    create: hr.createCase,
    transition: (r, to) => hr.setCaseStatus(r.id, { status: to }).then(() => clearLookup('employees')),
    statusField: 'status', workflow: TWO_STEP,
    search: ['case_number', 'employee_name', 'employee_no', 'title'],
    detailLink: r => ({ to: hrSubUrl('employee-360', { id: r.employee_id }), label: 'Open Employee 360' }),
    fields: [
      readonly({ name: 'case_number', label: 'Case No', list: true }),
      who(),
      { name: 'title', label: 'Title', required: true, list: true },
      { name: 'effective_date', label: 'Effective Date', type: 'date', required: true, list: true },
      ref('to_department_id', 'To Department', 'departments', 'to_department_name', { list: true }),
      ref('to_designation_id', 'To Designation', 'designations', 'to_designation_title'),
      ref('to_site_id', 'To Site', 'sites', 'to_site_name', { list: true }),
      ref('to_branch_id', 'To Branch', 'branches', 'to_branch_name'),
      readonly({ name: 'from_department_name', label: 'From Department' }),
      readonly({ name: 'from_designation_title', label: 'From Designation' }),
      readonly({ name: 'from_site_name', label: 'From Site' }),
      { name: 'details', label: 'Details', type: 'textarea' },
      readonly(status(APPROVALS)),
    ],
  },

  performance_reviews: {
    key: 'performance_reviews', label: 'Performance Review', title: r => `${r.employee_name} · ${r.review_period}`,
    load: () => hr.listReviews(),
    create: hr.createReview,
    statusField: 'status',
    search: ['employee_name', 'employee_no', 'review_period', 'reviewer_name', 'rating'],
    detailLink: r => ({ to: hrSubUrl('employee-360', { id: r.employee_id, tab: 'performance' }), label: 'Open in Employee 360' }),
    fields: [
      who(),
      { ...who('Reviewer', 'reviewer_id'), list: false, get: r => r.reviewer_name },
      { name: 'review_period', label: 'Review Period', required: true, list: true, placeholder: 'e.g. 2026-H1' },
      { name: 'review_date', label: 'Review Date', type: 'date', required: true, list: true },
      { name: 'kpi_score', label: 'KPI Score', type: 'number', min: 0, max: 100 },
      { name: 'attendance_score', label: 'Attendance Score', type: 'number', min: 0, max: 100 },
      { name: 'technical_score', label: 'Technical Score', type: 'number', min: 0, max: 100 },
      { name: 'compliance_score', label: 'Compliance Score', type: 'number', min: 0, max: 100 },
      { name: 'behaviour_score', label: 'Behaviour Score', type: 'number', min: 0, max: 100 },
      readonly({ name: 'overall_score', label: 'Overall Score', type: 'number', list: true }),
      readonly({ name: 'rating', label: 'Rating', list: true }),
      { name: 'recommended_promotion', label: 'Recommend Promotion', type: 'boolean', list: true },
      { name: 'recommended_merit_increase_pct', label: 'Merit Increase (%)', type: 'number', min: 0, max: 100 },
      { name: 'feedback', label: 'Reviewer Feedback', type: 'textarea' },
      { name: 'employee_comments', label: 'Employee Comments', type: 'textarea' },
      readonly(status(['draft', 'submitted', 'acknowledged', 'completed'])),
    ],
  },

  // ── Time & Attendance ────────────────────────────────────────────────────
  leave_requests: {
    key: 'leave_requests', label: 'Leave Request', title: r => `${r.reference} · ${r.employee_name}`,
    load: (f = {}) => hr.listLeaveRequests(f),
    create: hr.createLeaveRequest,
    // supervisor sign-off (pending) then HR (pre-approved); approval deducts the balance
    transition: (r, to) => hr.setLeaveRequestStatus(r.id, { status: to }),
    statusField: 'status', workflow: TWO_STEP,
    search: ['reference', 'employee_name', 'employee_no', 'leave_type_name', 'department_name', 'reason'],
    actions: (r, { canManage }) => [
      canManage && { label: r.attachment ? 'Replace Document' : 'Attach Document', fields: [LEAVE_ATTACHMENT], initial: x => x,
        run: (x, _, data) => LEAVE_ATTACHMENT.upload(x, data.attachment), done: 'Document uploaded' },
    ],
    detailLink: r => ({ to: hrSubUrl('employee-360', { id: r.employee_id, tab: 'leave' }), label: 'Open in Employee 360' }),
    fields: [
      readonly({ name: 'reference', label: 'Reference', list: true }),
      who(),
      readonly({ name: 'department_name', label: 'Department' }),
      ref('leave_type_id', 'Leave Type', 'leaveTypes', 'leave_type_name', { required: true, list: true }),
      { name: 'start_date', label: 'Start Date', type: 'date', required: true, list: true },
      { name: 'end_date', label: 'End Date', type: 'date', required: true, list: true },
      readonly({ name: 'days_requested', label: 'Working Days', type: 'integer', list: true }),
      readonly({ name: 'is_paid', label: 'Paid', type: 'boolean' }),
      { name: 'reason', label: 'Reason', type: 'textarea', required: true },
      { ...who('Reliever', 'reliever_employee_id'), required: false, list: false, get: r => r.reliever_name },
      { name: 'emergency_contact_phone', label: 'Contact While Away' },
      { name: 'handover_notes', label: 'Handover Notes', type: 'textarea' },
      LEAVE_ATTACHMENT,
      readonly({ name: 'approver_remarks', label: 'Approver Remarks' }),
      readonly(status(APPROVALS)),
    ],
  },

  salary_changes: {
    key: 'salary_changes', label: 'Salary Change', title: r => `${r.employee_name} · ${fmtDate(r.effective_date)}`,
    load: f => hr.listSalaryHistory(f.employee_id),
    create: hr.createSalaryChange,
    fields: [
      { ...who(), form: false },
      { name: 'effective_date', label: 'Effective Date', type: 'date', required: true, list: true, default: () => new Date().toISOString().slice(0, 10) },
      { name: 'change_type', label: 'Change Type', type: 'select', options: ['annual_review', 'promotion', 'merit', 'realignment'], default: 'annual_review', required: true, list: true, noBlank: true },
      readonly({ name: 'previous_basic_salary', label: 'Old Salary', type: 'money', list: true }),
      { name: 'new_basic_salary', label: 'New Basic Salary', type: 'money', required: true, list: true },
      readonly({ name: 'percentage_change', label: '% Change', type: 'number', list: true }),
      { name: 'reason', label: 'Reason', type: 'textarea', required: true, list: true },
    ],
  },




  missed_days: {
    key: 'missed_days', label: 'Missed Day', fields: [],
    notConnected: 'Missed Days is not connected yet: the HR backend has no missed-day records. Absences are tracked in Attendance Management until this register is built.',
  },

  // ── Welfare & ID ─────────────────────────────────────────────────────────
  accommodations: {
    key: 'accommodations', label: 'Accommodation', title: r => r.property_name,
    load: () => hr.listAccommodations(),
    create: async body => { await hr.createAccommodation(body); clearLookup('accommodations'); },
    statusField: 'status',
    search: ['property_name', 'address', 'site_name'],
    detailLink: () => ({ to: hrSubUrl('accommodation-assignments'), label: 'Accommodation Assignments' }),
    fields: [
      { name: 'property_name', label: 'Property Name', required: true, list: true },
      { name: 'address', label: 'Address', type: 'textarea' },
      ref('site_id', 'Project Site', 'sites', 'site_name', { list: true }),
      { name: 'capacity_beds', label: 'Capacity (beds)', type: 'integer', min: 1, required: true, list: true },
      readonly({ name: 'occupied_beds', label: 'Occupied', type: 'integer', list: true }),
      readonly({ name: 'free_beds', label: 'Free Beds', type: 'integer', list: true, get: r => Math.max(0, (r.capacity_beds || 0) - (r.occupied_beds || 0)) }),
      { name: 'monthly_rent', label: 'Monthly Rent', type: 'money', list: true },
      status(['available', 'full', 'maintenance'], { default: 'available', noBlank: true }),
    ],
  },

  accommodation_assignments: {
    key: 'accommodation_assignments', label: 'Accommodation Assignment', title: r => `${r.employee_name} · ${r.property_name || 'Housing support'}`,
    // assignments come nested in each property; the register flattens them
    load: () => hr.listAccommodations().then(list => list.flatMap(a => (a.active_assignments || []).map(x => ({ ...x, property_name: x.property_name || a.property_name })))),
    create: async ({ accommodation_id, ...body }) => { await hr.assignAccommodation(accommodation_id, { ...body, accommodation_id }); clearLookup('accommodations'); },
    statusField: 'status',
    search: ['employee_name', 'employee_no', 'property_name', 'unit_or_room'],
    actions: (r, { canManage }) => [canManage && r.status === 'active' && {
      label: 'Vacate', tone: 'danger', confirm: `Vacate ${r.employee_name} from ${r.property_name || 'this accommodation'} today?`,
      run: x => hr.vacateAssignment(x.id), done: 'Assignment vacated — bed released',
    }],
    fields: [
      who(),
      ref('accommodation_id', 'Property', 'accommodations', 'property_name', { required: true, list: true }),
      { name: 'support_type', label: 'Support Type', type: 'select', options: ['company_accommodation', 'housing_support'], default: 'company_accommodation', list: true, noBlank: true },
      { name: 'unit_or_room', label: 'Unit / Room', list: true },
      { name: 'monthly_amount', label: 'Monthly Amount', type: 'money', list: true },
      { name: 'start_date', label: 'Start Date', type: 'date', required: true, default: () => new Date().toISOString().slice(0, 10), list: true },
      { name: 'end_date', label: 'End Date', type: 'date' },
      status(['active', 'vacated', 'expired'], { default: 'active', noBlank: true }),
    ],
  },

  hmo_enrollments: {
    key: 'hmo_enrollments', label: 'HMO Enrollment', title: r => `${r.employee_name} · ${r.provider_name}`,
    load: () => hr.listHmo(),
    create: hr.enrollHmo,
    statusField: 'status',
    search: ['employee_name', 'employee_no', 'provider_name', 'policy_number', 'primary_hospital'],
    fields: [
      who(),
      { name: 'provider_name', label: 'HMO Provider', required: true, list: true, suggestions: ['Hygeia HMO', 'AXA Mansard', 'Reliance HMO', 'Leadway Health', 'Avon HMO', 'Clearline HMO'] },
      { name: 'plan_tier', label: 'Plan Tier', type: 'select', options: ['bronze', 'silver', 'gold', 'platinum'], default: 'silver', list: true, noBlank: true },
      { name: 'policy_number', label: 'Policy Number', list: true },
      { name: 'primary_hospital', label: 'Primary Hospital' },
      { name: 'dependents_count', label: 'Dependants Covered', type: 'integer', min: 0, default: 0, list: true },
      { name: 'monthly_premium', label: 'Monthly Premium', type: 'money', list: true },
      { name: 'effective_date', label: 'Effective Date', type: 'date' },
      status(['active', 'pending', 'suspended', 'ended'], { default: 'active', noBlank: true }),
    ],
  },

  id_cards: {
    key: 'id_cards', label: 'ID Card', title: r => `${r.employee_name} · ${r.card_number || 'Requested'}`,
    load: () => hr.listIdCards(),
    create: hr.requestIdCard,
    update: (r, body) => hr.updateIdCard(r.id, body),
    statusField: 'status',
    // printing and issue are status moves on the card itself
    workflow: { requested: ['printed'], printed: ['active'], active: ['expired', 'lost'] },
    transition: (r, to) => hr.updateIdCard(r.id, { status: to, ...(to === 'active' && !r.issue_date ? { issue_date: new Date().toISOString().slice(0, 10) } : {}) }),
    search: ['employee_name', 'employee_no', 'card_number', 'department_name'],
    detailLink: r => ({ to: hrSubUrl('employee-360', { id: r.employee_id, tab: 'idcards' }), label: 'Open in Employee 360' }),
    fields: [
      { ...who(), form: 'create' },
      { name: 'card_number', label: 'Card Number', list: true, placeholder: 'Assigned on print' },
      readonly({ name: 'department_name', label: 'Department', list: true }),
      readonly({ name: 'designation_title', label: 'Designation' }),
      { name: 'template_type', label: 'Template', type: 'select', options: ['standard', 'executive', 'contractor', 'visitor', 'expat'], default: 'standard', list: true, noBlank: true },
      { name: 'issue_date', label: 'Issue Date', type: 'date', list: true },
      { name: 'expiry_date', label: 'Expiry Date', type: 'date', list: true },
      { name: 'barcode_qr', label: 'Barcode / QR' },
      status(['requested', 'printed', 'active', 'expired', 'lost'], { default: 'requested', noBlank: true }),
    ],
  },
};
